import { Controller } from "@hotwired/stimulus"

// GDPR Block C — cookie banner controller.
//
// Mounted on <body> (alongside dark-mode) so the footer "Cookie settings" link,
// which lives outside the banner markup, can re-open the panel via
// data-action="click->cookie-consent#reopen".
//
// Intercepts the banner form submit to save the choice without a page reload,
// then hides the banner and drops any cookies the visitor just revoked.
// Without JS, the form degrades to a native POST + redirect (see turbo:false).
export default class extends Controller {
  static targets = ["banner", "modal", "status"]

  async submit(event) {
    const form = event.target
    const submitter = event.submitter
    event.preventDefault()

    const body = new FormData(form)
    if (submitter && submitter.name) body.append(submitter.name, submitter.value)

    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body,
        credentials: "same-origin",
        headers: { "X-CSRF-Token": token, Accept: "application/json" },
      })
      if (!response.ok) throw new Error(`cookie consent save failed: ${response.status}`)

      const choices = this.choicesFrom(body)
      this.dismiss()
      this.applyRevocation(choices)
      if (this.hasStatusTarget) this.statusTarget.hidden = false
    } catch (_error) {
      // Hard fallback: let the browser POST the form and follow the redirect.
      // Native submit() ignores the submitter, so carry its name/value in a
      // hidden input — otherwise "Accept all" would post no decision and the
      // server would silently record "necessary only".
      if (submitter && submitter.name) {
        const hidden = document.createElement("input")
        hidden.type = "hidden"
        hidden.name = submitter.name
        hidden.value = submitter.value
        form.appendChild(hidden)
      }
      form.submit()
    }
  }

  openSettings(event) {
    if (event) event.preventDefault()
    this.modalTarget.classList.remove("hidden")
  }

  closeSettings(event) {
    if (event) event.preventDefault()
    this.modalTarget.classList.add("hidden")
  }

  // Footer "Cookie settings" link: bring the banner back and open the panel.
  // Only meaningful where a banner exists; a no-op on the Settings page.
  reopen(event) {
    if (event) event.preventDefault()
    if (!this.hasBannerTarget) return
    this.bannerTarget.classList.remove("hidden")
    this.openSettings()
  }

  // Hide the banner/modal after a save. On the Settings page there is no banner,
  // so guard both targets — the form there saves in place.
  dismiss() {
    if (this.hasBannerTarget) this.bannerTarget.classList.add("hidden")
    if (this.hasModalTarget) this.closeSettings()
  }

  choicesFrom(body) {
    const decision = body.get("decision")
    if (decision === "all") return { analytics: true, marketing: true }
    if (decision === "necessary") return { analytics: false, marketing: false }
    return { analytics: body.get("analytics") === "1", marketing: body.get("marketing") === "1" }
  }

  // Revocation (C7): when a category is turned off, drop the cookies it set so a
  // withheld/withdrawn consent takes effect immediately, before any reload.
  applyRevocation(choices) {
    if (!choices.analytics) this.deleteCookies(["_ga", "_gid", "_gat"])
    if (!choices.marketing) this.deleteCookies(["_fbp", "_gcl_au"])
  }

  deleteCookies(prefixes) {
    // GA4/Meta set their cookies on the registrable domain (e.g. ".example.com"),
    // so a host-only delete (no domain) leaves them alive and tracking continues
    // after revocation. Delete each match twice — host-only and domain-scoped.
    const rootDomain = window.location.hostname.split(".").slice(-2).join(".")
    document.cookie.split(";").forEach((cookie) => {
      const name = cookie.split("=")[0].trim()
      if (prefixes.some((prefix) => name === prefix || name.startsWith(prefix))) {
        document.cookie = `${name}=; Max-Age=0; path=/`
        document.cookie = `${name}=; Max-Age=0; path=/; domain=.${rootDomain}`
      }
    })
  }
}
